import type { Element } from "../schema/elements";
import type { Transform } from "../schema/primitives";

export interface BBox { x: number; y: number; w: number; h: number }

/** Axis-aligned bounds of a rotated rectangle. */
export function rotatedBounds(t: Transform): BBox {
  const rad = (t.rot * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
  const w = t.w * cos + t.h * sin;
  const h = t.w * sin + t.h * cos;
  const cx = t.x + t.w / 2, cy = t.y + t.h / 2;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

/**
 * Find an element by id and compute its axis-aligned bounds in slide space, composing
 * group child-space transforms (a:chOff / a:chExt) along the way.
 */
export function elementBBox(elements: Element[], id: string): BBox | null {
  const visit = (els: Element[], map: (b: BBox) => BBox): BBox | null => {
    for (const el of els) {
      const own = map(rotatedBounds(el.transform));
      if (el.id === id) return own;
      if (el.kind === "group") {
        const { childSpace, transform } = el;
        const sx = childSpace.w ? transform.w / childSpace.w : 1;
        const sy = childSpace.h ? transform.h / childSpace.h : 1;
        const inner = (b: BBox): BBox =>
          map({
            x: transform.x + (b.x - childSpace.x) * sx,
            y: transform.y + (b.y - childSpace.y) * sy,
            w: b.w * sx,
            h: b.h * sy,
          });
        const found = visit(el.children, inner);
        if (found) return found;
      }
    }
    return null;
  };
  return visit(elements, (b) => b);
}
